import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Lock, Undo2 } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";

export function CostSlider({
    form,
    product,
    shop,
    selectedMarket,
    optimizationSettings,
    setOptimizationSettings,
    recalculateFormValues,
    productPrice,
}: {
    form: any;
    product: any;
    shop: any;
    selectedMarket: any;
    optimizationSettings: any;
    setOptimizationSettings: any;
    recalculateFormValues: any;
    productPrice: number;
}) {
    const isLocked = product.shopify_unit_cost !== null;
    const { currencySymbol } = useCurrency();

    const defaultCost =
        product.price * 0.01 * shop?.settings?.optimizations.cost;

    let isOverridden = false;
    if (selectedMarket === "all") {
        if (defaultCost !== optimizationSettings?.base_market?.cost) {
            isOverridden = true;
        }
    } else {
        if (
            optimizationSettings?.markets?.[selectedMarket]?.cost !== undefined
        ) {
            isOverridden = true;
        }
    }

    const updateCost = (value: number) => {
        // Update form field value
        form.setValue("cost", value);

        if (selectedMarket === "all") {
            setOptimizationSettings({
                ...optimizationSettings,
                base_market: {
                    ...optimizationSettings.base_market,
                    cost: value,
                },
            });
        } else {
            setOptimizationSettings({
                ...optimizationSettings,
                markets: {
                    ...optimizationSettings.markets,
                    [selectedMarket]: {
                        ...optimizationSettings.markets[selectedMarket],
                        cost: value,
                    },
                },
            });
        }
    };

    const handleBaseReset = () => {
        if (selectedMarket === "all") {
            setOptimizationSettings({
                ...optimizationSettings,
                base_market: {
                    ...optimizationSettings.base_market,
                    cost: defaultCost,
                },
            });
        } else {
            // Remove cost from optimization settings
            setOptimizationSettings({
                ...optimizationSettings,
                markets: {
                    ...optimizationSettings.markets,
                    [selectedMarket]: {
                        ...optimizationSettings.markets[selectedMarket],
                        cost: undefined,
                    },
                },
            });
        }

        recalculateFormValues();
    };

    return (
        <FormField
            control={form.control}
            name="cost"
            render={({ field }) => (
                <FormItem>
                    <FormLabel className="flex items-center gap-2">
                        Cost price ({currencySymbol}
                        {field.value.toFixed(2)})
                        {isLocked && (
                            <div className="flex items-center gap-1">
                                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                                Locked
                            </div>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={(e) => {
                                e.preventDefault();
                                handleBaseReset();
                            }}
                            disabled={!isOverridden || isLocked}
                        >
                            <Undo2 className="h-3.5 w-3.5" />
                            Reset
                        </Button>
                    </FormLabel>
                    <FormDescription>
                        {isLocked
                            ? "Cost price is locked because the unit cost is set in Shopify. To adjust the cost price, you must first update the unit cost in Shopify or remove it and adjust the cost price here."
                            : "What is the unit cost of this product?"}
                    </FormDescription>
                    <div className="flex gap-4">
                        <FormControl
                            className={`flex-1 ${isLocked ? "opacity-50" : ""}`}
                        >
                            <Slider
                                min={0}
                                max={productPrice * 2}
                                step={0.01}
                                value={[Number(field.value.toFixed(2))]}
                                onValueChange={(value) =>
                                    updateCost(Number(value[0].toFixed(2)))
                                }
                                disabled={isLocked}
                            />
                        </FormControl>
                        <FormControl>
                            <input
                                type="number"
                                className="w-24 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors"
                                min={0}
                                max={product.price * 2}
                                step={0.01}
                                value={Number(field.value.toFixed(2))}
                                onChange={(e) => {
                                    const value =
                                        e.target.value === ""
                                            ? 0
                                            : Number(e.target.value);
                                    updateCost(value);
                                }}
                                disabled={isLocked}
                            />
                        </FormControl>
                    </div>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
